import * as React from 'react'
import { Button, PageHeader } from '@pushly/aqe/lib/components'
import type { ButtonAction } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import { Loading3QuartersOutlined, DollarOutlined } from '@ant-design/icons'
import { Table, DatePicker, Flex } from 'antd'
import './style/batch-billing.scss'
import { AppService } from '@halo/services/app.service'
import { AccountService } from '@halo/services/account.service'
import { ColumnProps } from 'antd/es/table'
import titleCase from 'title-case'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import clone from 'clone-deep'
import { AccountChargeForm } from '@halo/components/account-charge-form/account-charge-form'
import moment, { Moment } from 'moment-timezone'
import { Dayjs } from 'dayjs'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { useService } from '@halo/hooks/use-service'

interface BillableAccountRow {
    id: number
    name: string
    billing_data?: any
    created_at?: string
    loadingDependecies?: boolean
    lastBillingRecord?: any
    lastMonthsBillableSubscriberCount?: number
}

interface IColumnProps extends ColumnProps<BillableAccountRow> {
    dataKey?: string
}

interface IAccounts {}

interface IAccountsState {
    loading: boolean
    accountsLoaded: boolean
    allAccounts: BillableAccountRow[]
    visibleAccounts: BillableAccountRow[]
    accountsMeta: any
    sortedInfo: any
    page: number
    pageSize: number
    query?: string

    unchargedSince: string
    activeAccount?: BillableAccountRow
}

export const BatchBilling = (props: React.PropsWithChildren<IAccounts>) => {
    const appSvc = useService(AppService)
    const accountSvc = useService(AccountService)

    const lastMonthValue = moment().subtract(1, 'month').startOf('month')

    const [state, setState] = React.useState<IAccountsState>({
        page: 1,
        pageSize: 15,
        loading: true,
        accountsLoaded: false,
        allAccounts: [],
        visibleAccounts: [],
        accountsMeta: {},
        sortedInfo: {
            order: 'ascend',
            columnKey: 'name',
        },
        unchargedSince: lastMonthValue.format('YYYY-MM'),
    })

    const isLoading = state.loading && !state.accountsLoaded

    const fetchAccounts = async () => {
        setState((prev) => ({ ...prev, loading: true, accountsLoaded: false }))
        let update: Partial<IAccountsState>

        try {
            const { ok, data, meta } =
                (await accountSvc.fetchBillable(
                    {
                        pagination: 0,
                        status: ['active'].join(','),
                        billable_period: state.unchargedSince,
                    },
                    false,
                )) || {}

            if (ok) {
                const accountRows: BillableAccountRow[] = data.map((account) => ({
                    ...account,
                    loadingDependecies: true,
                }))

                update = {
                    accountsLoaded: true,
                    allAccounts: accountRows,
                    visibleAccounts: accountRows,
                    accountsMeta: meta,
                }
            }
        } catch (error) {
            console.warn(error)
        }

        setState({
            loading: false,
            ...(update as any),
        })
    }

    /**
     * Load on mount
     */
    React.useEffect(() => {
        if (state.accountsLoaded === false) {
            fetchAccounts()
        }
    }, [])

    /**
     * Load on successful account fetch and begin nested account network calls for specific billing history
     *
     * Starts a transition for each account (loaded as rows in consuming table) network call
     * to cleanly suspend rendering at the appropriate nodes while the rest of the UI is interactable
     */
    React.useEffect(() => {
        const fetchAccountDependencies = async (account: BillableAccountRow, idx: number) => {
            const update = clone(account) as BillableAccountRow
            update.loadingDependecies = false

            const processes: Array<Promise<any>> = []

            processes.push(
                accountSvc
                    .fetchBillingRecordsByAccountId(
                        account.id,
                        {
                            limit: 1,
                            order: JSON.stringify({
                                field: 'record.created_at',
                                direction: 'desc',
                            }),
                            processed: 1,
                        },
                        false,
                    )
                    .then(({ ok, data }) => {
                        if (ok) {
                            update.lastBillingRecord = data.length > 0 ? data[0] : null
                        }
                    }),
            )

            const lastMonth = moment().subtract(1, 'month')
            processes.push(
                accountSvc
                    .fetchSubscriberHistoryByAccountId(
                        account.id,
                        {
                            date_range: JSON.stringify({
                                since: lastMonth.startOf('month').format('YYYY-MM-DD'),
                                through: lastMonth.endOf('month').format('YYYY-MM-DD'),
                            }),
                        },
                        false,
                    )
                    .then(({ ok, data }) => {
                        if (ok) {
                            update.lastMonthsBillableSubscriberCount =
                                data.length > 0 ? data[0].subscribed_count : 28045
                        }
                    }),
            )

            await Promise.all(processes)

            return setState(({ allAccounts, ...prev }) => {
                const newAccounts = [...allAccounts]
                newAccounts[idx] = update

                return { ...prev, allAccounts: newAccounts, visibleAccounts: newAccounts }
            })
        }

        if (state.accountsLoaded && state.allAccounts.length > 0) {
            state.allAccounts.forEach((account, idx) => {
                fetchAccountDependencies(account, idx)
            })
        }
    }, [state.allAccounts.length])

    let { sortedInfo } = state
    sortedInfo = sortedInfo || {}

    const buildColumn = (config: IColumnProps): React.ReactNode => {
        const title = (config.title as string) || titleCase(config.dataKey || (config.key as string))
        if (config.sorter) {
            config.sortOrder = sortedInfo.columnKey === (config.dataKey || config.key) && sortedInfo.order
        }

        return (
            <Table.Column
                {...config}
                title={title}
                dataIndex={config.dataKey}
                key={config.dataKey || config.key}
                className={config.className || config.dataKey || (config.key as string)}
            />
        )
    }

    const updateSelectedPeriod = async (billablePeriod: Moment | undefined): Promise<void> => {
        if (!!billablePeriod) {
            await setState((prev) => ({ ...prev, unchargedSince: billablePeriod.startOf('month').format('YYYY-MM') }))
        } else {
            await setState((prev) => ({ ...prev, unchargedSince: undefined }))
        }

        return fetchAccounts()
    }

    const renderDisplay = (text: string, account: BillableAccountRow): React.ReactNode => {
        const name = account.name

        return <TableRowEntityDisplay title={name} />
    }

    const renderLastBilledAt = (text: string, account: BillableAccountRow): React.ReactNode => {
        if (account.loadingDependecies) {
            return <Loading3QuartersOutlined spin={true} />
        }

        if (!account.lastBillingRecord) {
            return '--'
        }

        let receiptUrl: string
        if (account.lastBillingRecord.stripe_charge_receipt_url) {
            receiptUrl = account.lastBillingRecord.stripe_charge_receipt_url
        }

        const date = moment(account.lastBillingRecord.created_at).format('LL')

        return !receiptUrl ? (
            date
        ) : (
            <a href={receiptUrl} target="_blank">
                {date}
            </a>
        )
    }

    const renderRowActions = (account: BillableAccountRow): React.ReactNode => {
        const actions: ButtonAction[] = [
            {
                key: 'history',
                text: 'Billing History',
                icon: <DollarOutlined />,
                onClick: (domEvent) =>
                    handleDropdownActionClick('history', account, domEvent as React.MouseEvent<HTMLElement>),
            },
        ]

        return (
            <Button size="small" onClick={() => handlePrimaryActionClick(account)} actions={actions}>
                New Charge
            </Button>
        )
    }

    const handleChange = (pagination: any, filters: any, sorter: any) => {
        setState((prev) => ({
            ...prev,
            sortedInfo: sorter,
        }))
    }

    const handlePrimaryActionClick = (account: BillableAccountRow): void => {
        setState((prev) => ({ ...prev, activeAccount: account }))
    }

    const handleDropdownActionClick = (
        key: string | number,
        account: BillableAccountRow,
        domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ) => {
        if (key === 'history') {
            if (domEvent.metaKey) {
                window.open(buildBillingHistoryUrl(account))
            } else {
                jumpToBillingHistory(account)
            }
        }
    }

    const buildBillingHistoryUrl = (account: BillableAccountRow): string => {
        return `/organizations/${account.id}#billing`
    }

    const jumpToBillingHistory = (account: BillableAccountRow) => {
        appSvc.route(buildBillingHistoryUrl(account))
    }

    const tableHeader = (
        <Flex className="batch-billing-list-header" gap={8} justify="space-between" align="center">
            <Flex flex={1} gap={8} align="center" justify="flex-end">
                <span>Show uncharged accounts for: </span>
                <DatePicker.MonthPicker
                    size="middle"
                    placeholder=""
                    format="MMMM, YYYY"
                    defaultValue={lastMonthValue as any}
                    disabledDate={(current: Dayjs) => {
                        return current.startOf('month').valueOf() > lastMonthValue.valueOf()
                    }}
                    onChange={updateSelectedPeriod as any}
                />
            </Flex>
        </Flex>
    )

    return (
        <div className={classnames('page', 'batch-billing')}>
            <div className={classnames('page-wrapper')}>
                <PageHeader title="Batch Billing" onTitleSet={noop} />

                <Table<BillableAccountRow>
                    className="aqe-table-well"
                    title={() => tableHeader}
                    size="small"
                    bordered={false}
                    dataSource={state.visibleAccounts}
                    onChange={handleChange}
                    pagination={false}
                    loading={
                        isLoading && {
                            indicator: <Loading3QuartersOutlined spin={true} />,
                        }
                    }
                    locale={{
                        emptyText: <AntdTableEmptyPlaceholder text="No Accounts" icon={<DollarOutlined />} />,
                    }}
                    rowKey={(record: BillableAccountRow) => `${record.id}.${record.lastBillingRecord ? 1 : 0}`}
                >
                    {buildColumn({
                        className: 'account',
                        dataKey: 'display_name',
                        title: 'Organization',
                        render: renderDisplay,
                    })}
                    {buildColumn({
                        key: 'last_billed_at',
                        title: 'Last Billed',
                        render: renderLastBilledAt,
                    })}
                    {buildColumn({
                        key: 'actions',
                        title: '',
                        render: (notification) => renderRowActions(notification),
                        align: 'center',
                    })}
                </Table>

                <AccountChargeForm
                    key={`charge-form-act${state.activeAccount ? state.activeAccount.id : '0'}-${
                        state.unchargedSince || '0'
                    }`}
                    visible={!!state.activeAccount}
                    account={state.activeAccount as any}
                    onClose={() => setState((prev) => ({ ...prev, activeAccount: undefined }))}
                    onSuccess={noop}
                    defaultBillingPeriod={state.unchargedSince}
                />
            </div>
        </div>
    )
}
